// Ääntämisvalmentaja (Axel 2026-10-12): meter honesty and tuning takes. What a sound's judges can do, said before
// practice (the hub row's tag, the guide page, the top of the live step: engine/src/rules/coach-measure.ts), the
// "harjoiteltu" mark of a sound nobody can judge yet (SELF_PASS), and the opt-in switch that keeps the person's own
// takes for tuning the meters (app/src/coach/tuning.ts). Style: docs/FINNISH-STYLE.md (sinä, no jargon). en: the same shape.
import { defineArea } from '../area';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const coachHonestyFi = {
  /** The hub row's small tag. */
  tag: {
    unmeasured: 'ei mitata vielä',
    cautious: 'mittari varovainen',
    measurement: 'näyttää, ei arvioi',
  },
  /** The note on the guide page and at the top of the live step. */
  note: {
    unmeasured: 'Tätä äännettä ei vielä mitata: harjoittele kuuntelemalla Petersonia ja vertaa omaan ottoosi.',
    cautiousR: 'r-mittari on varovainen: se tunnistaa vain selvän tärinän, joten se sanoo usein ”en ole varma”.',
    cautious: 'Tämän äänteen mittari on varovainen: se sanoo usein ”en ole varma”.',
    measurement: 'Mittari näyttää sävelkulun ja painon, mutta ei arvioi niitä: vertaa omaa ottoasi Petersoniin.',
  },
  live: {
    /** After a take of a sound nobody judges. */
    compare: 'Kuuntele ottosi ja vertaa sitä Petersoniin.',
    takes: (n: number) => `Ottoja tällä kertaa: ${n}`,
  },
  /** The mark of a step nobody can judge yet. */
  self: {
    rule: (n: number) =>
      `Tätä askelta ei voi vielä arvioida, joten merkitset sen itse harjoitelluksi, kun olet tehnyt vähintään ${n} ${plural(n, 'otto', 'ottoa')} ja verrannut niitä Petersoniin.`,
    more: (n: number) => `Tee vielä ${n} ${plural(n, 'otto', 'ottoa')}, niin voit merkitä askeleen harjoitelluksi.`,
    mark: 'Merkitse harjoitelluksi',
    marked: (date: string) => `Merkitsit askeleen harjoitelluksi ${date}.`,
    undo: 'Peru merkintä',
  },
  tuning: {
    title: 'Ottojen tallennus',
    label: 'Tallenna ottoni mittarien säätöä varten',
    on: 'Päällä',
    off: 'Pois',
    note: 'Kun tämä on päällä, jokainen arvioitu ottosi tallennetaan mittaustietoineen, jotta mittareita voidaan säätää sinun äänesi mukaan. Vain omat ottosi tallennetaan, eikä kumppanisi näe niitä. Ne säilyvät, kunnes poistat ne. Kumppanisi ottoja ei tallenneta, ellei hän itse ota tätä käyttöön.',
    kept: (n: number, mb: number) => `Tallessa ${n} ${plural(n, 'otto', 'ottoa')} (${mb.toFixed(1).replace('.', ',')} Mt).`,
    none: 'Ei tallennettuja ottoja.',
    deleteAll: 'Poista kaikki tallennetut otot',
    confirm: 'Poistetaanko kaikki tallennetut otot? Poistoa ei voi perua.',
    deleted: 'Otot on poistettu.',
    failed: 'Poisto ei onnistunut. Yritä uudelleen, kun yhteys toimii.',
    offline: 'Asetusta ei voi nyt muuttaa. Yritä uudelleen, kun yhteys toimii.',
  },
};

export const coachHonestyEn: typeof coachHonestyFi = {
  tag: {
    unmeasured: 'not measured yet',
    cautious: 'cautious meter',
    measurement: 'shows, does not judge',
  },
  note: {
    unmeasured: 'This sound is not measured yet: practise by listening to Peterson and compare with your own take.',
    cautiousR: 'The r meter is cautious: it recognises only a clear trill, so it often says “not sure”.',
    cautious: 'This sound’s meter is cautious: it often says “not sure”.',
    measurement: 'The meter shows the pitch and the stress but does not judge them: compare your take with Peterson’s.',
  },
  live: {
    compare: 'Listen to your take and compare it with Peterson’s.',
    takes: (n) => `Takes this time: ${n}`,
  },
  self: {
    rule: (n) => `This step cannot be judged yet, so you mark it practised yourself once you have made at least ${n} takes and compared them with Peterson’s.`,
    more: (n) => `Make ${n} more ${plural(n, 'take', 'takes')} and you can mark the step practised.`,
    mark: 'Mark as practised',
    marked: (date) => `You marked the step practised on ${date}.`,
    undo: 'Undo the mark',
  },
  tuning: {
    title: 'Saving takes',
    label: 'Save my takes for tuning the meters',
    on: 'On',
    off: 'Off',
    note: 'While this is on, every judged take of yours is saved with its audio and measurements so the meters can be tuned to your voice. Only your own takes are saved, and your partner cannot see them. They are kept until you delete them. Your partner’s takes are never saved unless they turn this on themselves.',
    kept: (n, mb) => `${n} ${plural(n, 'take', 'takes')} kept (${mb.toFixed(1)} MB).`,
    none: 'No takes saved.',
    deleteAll: 'Delete all saved takes',
    confirm: 'Delete all saved takes? This cannot be undone.',
    deleted: 'The takes are deleted.',
    failed: 'Deleting did not work. Try again when the connection works.',
    offline: 'The setting cannot be changed now. Try again when the connection works.',
  },
};

// Messages.coachHonesty (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('coachHonesty', coachHonestyFi, coachHonestyEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof coachHonestyFi> {}
}
